import { FC, useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Modal,
  Button,
  Spin,
  Tag,
  Alert,
  Descriptions,
  Space,
  Typography,
  message,
} from 'antd';
import {
  CompassOutlined,
  UserOutlined,
  ProjectOutlined,
  ApiOutlined,
  SwapOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { userInformationAtom } from '~/stores/UserStore';
import {
  isMapLoadedDoneAtom,
  mapDataListAtom,
  showLegendPanelAtom,
} from '~/components/Map/stores/mapStore';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { ShareMapDataProjectAPIs } from '~/common/typescript/apis/ShareMapDataProjectAPIs';
import { getAxiosInstance } from '~/common/react/utils/networkUtil';
import { API_URL_ADMIN } from '~/config/config';
import MapDataUtils from '../../utils/MapDataUtils';
import { LayerItemUtil } from '../LegendPanel/LegendTab/LayerItem/LayerItemUtil';

const { Text, Title, Paragraph } = Typography;

export const AddMapDataModal: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentProject = useAtomValue(currentProjectAtom);
  const userInfo = useAtomValue(userInformationAtom);
  const isMapLoadedDone = useAtomValue(isMapLoadedDoneAtom);
  const [mapDataList, setMapDataList] = useAtom(mapDataListAtom);
  const setShowLegendPanel = useSetAtom(showLegendPanelAtom);

  const action = searchParams.get('action');
  const mapDataIdParam = searchParams.get('map_data_id');
  const isOpen = action === 'add_map_data' && Boolean(mapDataIdParam);
  const mapDataId = mapDataIdParam ? Number(mapDataIdParam) : null;

  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [mapData, setMapData] = useState<MapData_Base | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [detectedLocal, setDetectedLocal] = useState<boolean>(false);
  const [detectedProd, setDetectedProd] = useState<boolean>(false);

  const currentApiBaseUrl =
    (typeof window !== 'undefined' && localStorage.getItem('API_URL_ADMIN')) ||
    API_URL_ADMIN;

  const handleClose = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    nextParams.delete('map_data_id');
    setSearchParams(nextParams, { replace: true });
    setMapData(null);
    setErrorStatus(null);
    setDetectedLocal(false);
    setDetectedProd(false);
  }, [searchParams, setSearchParams]);

  const fetchMapDataInfo = useCallback(async () => {
    if (!mapDataId || isNaN(mapDataId)) return;

    setLoading(true);
    setErrorMessage('');
    setErrorStatus(null);
    setDetectedLocal(false);
    setDetectedProd(false);

    try {
      // Use hideSystemError to prevent unhelpful generic toast
      const res = await getAxiosInstance({ hideSystemError: true }).get(
        `/api/map-data-ext/get-one/${mapDataId}`,
        { hideGlobalError: true } as any
      );

      if (res.data) {
        setMapData(res.data);
      } else {
        throw new Error('Dữ liệu trả về rỗng');
      }
    } catch (err: any) {
      console.warn('Failed to load map data info:', err);
      const status = err?.response?.status || 500;
      setErrorStatus(status);
      setErrorMessage(
        err?.response?.data?.error?.i18n?.vi ||
          err?.response?.data?.error?.message ||
          'Không tìm thấy dữ liệu bản đồ trên máy chủ hiện tại'
      );

      // Probe check alternate server for helpful developer/user suggestion
      const isCurrentlyLocal =
        currentApiBaseUrl.includes('localhost') ||
        currentApiBaseUrl.includes('127.0.0.1');

      if (!isCurrentlyLocal) {
        // Current is production -> check if it exists on local
        try {
          const probe = await axios.get(
            `http://localhost:11000/api/map-data-ext/get-one/${mapDataId}`,
            { timeout: 1500 }
          );
          if (probe.status === 200 && probe.data?.id) {
            setDetectedLocal(true);
          }
        } catch {
          // not found on local
        }
      } else {
        // Current is local -> check if it exists on production
        try {
          const probe = await axios.get(
            `https://strapi.admin.tlgeo.xyz/api/map-data-ext/get-one/${mapDataId}`,
            { timeout: 2000 }
          );
          if (probe.status === 200 && probe.data?.id) {
            setDetectedProd(true);
          }
        } catch {
          // not found on prod
        }
      }
    } finally {
      setLoading(false);
    }
  }, [mapDataId, currentApiBaseUrl]);

  useEffect(() => {
    if (isOpen && mapDataId) {
      fetchMapDataInfo();
    }
  }, [isOpen, mapDataId, fetchMapDataInfo]);

  const handleConfirmAdd = async () => {
    if (!mapData || !currentProject?.id) {
      message.error('Không đủ thông tin dự án hoặc dữ liệu để thực hiện');
      return;
    }

    setSubmitting(true);
    const hideMsg = message.loading('Đang thêm lớp dữ liệu vào dự án...', 0);

    try {
      // 1. Link to project
      try {
        await ShareMapDataProjectAPIs.remote_addMapDataToProject({
          projectId: currentProject.id,
          mapDataId: mapData.id,
        });
      } catch (linkErr) {
        console.warn('MapData might already be added to project:', linkErr);
      }

      // 2. Mark visible and update mapDataListAtom
      const visibleItem = { ...mapData, visible: true };
      setMapDataList((prev) => {
        const exists = prev.some((d) => d.id === mapData.id);
        if (exists) {
          return prev.map((d) => (d.id === mapData.id ? { ...d, visible: true } : d));
        }
        return [visibleItem, ...prev];
      });

      // 3. Render on MapLibre
      await MapDataUtils.addToMap(visibleItem);

      // 4. Fit bounds to layer
      await LayerItemUtil.zoomToLayer(visibleItem);

      // 5. Open Legend Panel
      setShowLegendPanel(true);

      message.success(`Đã thêm lớp dữ liệu "${mapData.name}" vào dự án thành công!`);
      handleClose();
    } catch (err: any) {
      console.error('Error adding map data to project:', err);
      message.error(err?.message || 'Không thể thêm dữ liệu bản đồ vào dự án');
    } finally {
      hideMsg();
      setSubmitting(false);
    }
  };

  const handleSwitchServer = (targetUrl: string) => {
    localStorage.setItem('API_URL_ADMIN', targetUrl);
    message.info(`Đang chuyển kết nối sang máy chủ ${targetUrl}...`);
    setTimeout(() => {
      window.location.reload();
    }, 400);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={580}
      centered
      destroyOnClose
      title={
        <Space align="center" style={{ fontSize: 16 }}>
          <CompassOutlined style={{ color: '#1677ff', fontSize: 20 }} />
          <span>Thêm dữ liệu bản đồ vào dự án</span>
        </Space>
      }
    >
      <div style={{ marginTop: 12 }}>
        {/* Context metadata: Project, User, Server */}
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>
              <ProjectOutlined style={{ marginRight: 6, color: '#0284c7' }} />
              Dự án đích:
            </span>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>
              {currentProject?.name || 'Chưa mở dự án'} (ID: {currentProject?.id || '—'})
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>
              <UserOutlined style={{ marginRight: 6, color: '#16a34a' }} />
              Tài khoản hiện tại:
            </span>
            <span>
              {userInfo ? (
                <>
                  <Text strong>{userInfo.fullname || userInfo.username}</Text>{' '}
                  <Text type="secondary">({userInfo.email})</Text>
                  {userInfo.gcTier && (
                    <Tag color="blue" style={{ marginLeft: 6 }}>
                      {userInfo.gcTier}
                    </Tag>
                  )}
                </>
              ) : (
                <Tag color="orange">Chưa đăng nhập</Tag>
              )}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              <ApiOutlined style={{ marginRight: 6, color: '#7c3aed' }} />
              Máy chủ GeoCloud:
            </span>
            <Tag color={currentApiBaseUrl.includes('localhost') ? 'cyan' : 'purple'}>
              {currentApiBaseUrl}
            </Tag>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '36px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#64748b' }}>
              Đang xác thực và tải thông tin lớp dữ liệu #{mapDataId}...
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && (errorStatus || !mapData) && (
          <div>
            <Alert
              type="error"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={
                <Text strong style={{ fontSize: 15 }}>
                  {detectedLocal
                    ? 'Dữ liệu bản đồ nằm trên máy chủ Local (chưa có trên Production)'
                    : detectedProd
                    ? 'Dữ liệu bản đồ nằm trên máy chủ Production'
                    : 'Không tìm thấy dữ liệu bản đồ hoặc tài khoản không có quyền'}
                </Text>
              }
              description={
                <div style={{ marginTop: 8, fontSize: 13, color: '#475569' }}>
                  <Paragraph style={{ marginBottom: 8 }}>
                    Hệ thống không thể tải thông tin lớp dữ liệu <b>#{mapDataId}</b> từ máy chủ{' '}
                    <code>{currentApiBaseUrl}</code>.
                  </Paragraph>

                  {detectedLocal && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Phát hiện lệch môi trường máy chủ (Server Mismatch)"
                      description={
                        <div>
                          Lớp dữ liệu <b>#{mapDataId}</b> đã được tìm thấy trên Strapi Local (
                          <code>http://localhost:11000</code>), trong khi GeoCloud hiện tại đang kết nối với máy
                          chủ Production (<code>{currentApiBaseUrl}</code>).
                        </div>
                      }
                    />
                  )}

                  {detectedProd && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Phát hiện dữ liệu trên máy chủ Production"
                      description={
                        <div>
                          Lớp dữ liệu <b>#{mapDataId}</b> được tìm thấy trên máy chủ Production (
                          <code>https://strapi.admin.tlgeo.xyz</code>), nhưng GeoCloud đang kết nối tới Local (
                          <code>{currentApiBaseUrl}</code>).
                        </div>
                      }
                    />
                  )}

                  <div style={{ background: '#ffffff', padding: 10, borderRadius: 6, border: '1px solid #f1f5f9' }}>
                    <b>Giải thích chi tiết:</b>
                    <ul style={{ paddingLeft: 18, marginTop: 4, marginBottom: 0 }}>
                      {detectedLocal ? (
                        <li>
                          <b>Tài khoản của bạn:</b> Bạn đang đăng nhập <code>{userInfo?.email || 'admin'}</code> với quyền{' '}
                          <b>{userInfo?.gcTier || 'ADMIN'}</b> (hoàn toàn có đủ quyền hạn). Tuy nhiên, dữ liệu bản đồ{' '}
                          <b>#{mapDataId}</b> hiện chỉ mới được tạo ở <b>Strapi Local (http://localhost:11000)</b>, chưa tồn
                          tại trên máy chủ Production <code>{currentApiBaseUrl}</code>.
                        </li>
                      ) : (
                        <>
                          <li>
                            <b>Quyền tài khoản:</b> Bạn đang đăng nhập tài khoản{' '}
                            <code>{userInfo?.email || 'Khách (chưa đăng nhập)'}</code>. Dữ liệu có thể thuộc tài khoản
                            hoặc tổ chức khác chưa được chia sẻ.
                          </li>
                          <li>
                            <b>Môi trường Backend:</b> Dữ liệu vừa tải lên ở môi trường GeoAdmin khác với máy chủ API
                            hiện tại của GeoCloud.
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              }
            />

            {/* Error Actions */}
            <div
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {detectedLocal && (
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={() => handleSwitchServer('http://localhost:11000')}
                >
                  Đổi sang Local (localhost:11000) & Xem ngay
                </Button>
              )}

              {detectedProd && (
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={() => handleSwitchServer('https://strapi.admin.tlgeo.xyz')}
                >
                  Đổi sang Production & Xem ngay
                </Button>
              )}

              <Button
                icon={<ReloadOutlined />}
                onClick={fetchMapDataInfo}
              >
                Thử lại
              </Button>

              <Button
                icon={<UserOutlined />}
                onClick={() => {
                  navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
                }}
              >
                Đổi tài khoản
              </Button>

              <Button onClick={handleClose}>Đóng</Button>
            </div>
          </div>
        )}

        {/* Success / MapData Preview */}
        {!loading && mapData && (
          <div>
            <div
              style={{
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: 16,
                background: '#ffffff',
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <FolderOpenOutlined style={{ fontSize: 28, color: '#1677ff', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ margin: 0, color: '#0f172a' }}>
                    {mapData.name}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Mã dữ liệu: #{mapData.id} • Phân loại: {mapData.category || 'USER_DATA'}
                  </Text>
                </div>
                <Tag color={mapData.processStatus === 'SUCCESS' ? 'success' : 'processing'}>
                  {mapData.processStatus || 'READY'}
                </Tag>
              </div>

              {mapData.description && (
                <Paragraph
                  type="secondary"
                  ellipsis={{ rows: 2 }}
                  style={{ marginTop: 10, marginBottom: 8, fontSize: 13 }}
                >
                  {mapData.description}
                </Paragraph>
              )}

              <Descriptions size="small" column={2} style={{ marginTop: 12 }}>
                <Descriptions.Item label="Chủ sở hữu">
                  {mapData.owner?.fullname || mapData.owner?.email || 'Hệ thống'}
                </Descriptions.Item>
                <Descriptions.Item label="Thời gian tạo">
                  {mapData.createdAt ? new Date(mapData.createdAt).toLocaleDateString('vi-VN') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Tệp tài sản" span={2}>
                  <Space size={[4, 4]} wrap>
                    {mapData.map_data_files && mapData.map_data_files.length > 0 ? (
                      mapData.map_data_files.map((file) => (
                        <Tag key={file.id} color="geekblue">
                          {file.type?.toUpperCase()}
                        </Tag>
                      ))
                    ) : (
                      <Text type="secondary">Chưa có tệp MBTiles/Vector</Text>
                    )}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={handleClose} disabled={submitting}>
                Hủy bỏ
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={submitting}
                onClick={handleConfirmAdd}
              >
                Thêm vào dự án & Xem ngay
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddMapDataModal;
